<template>
  <TappButton v-editable="props.blok" v-bind="button" @click="scrollToInPageTarget">
    <template v-if="icon" #default>
      {{ props.blok.text }}
      <TappIcon :icon :size="16" />
    </template>
  </TappButton>
</template>

<script setup lang="ts">
  import { determineIfTappIcon } from '@tapp/layer-design-system/components/TappIcon/TappIcon.vue';

  const props = defineProps<{ blok: BlokTappButtonStoryblok }>();

  const icon = computed(() => {
    const { icon } = props.blok;

    if (!icon) return;

    if (!determineIfTappIcon(icon)) return;

    return icon;
  });

  const button = computed(() => {
    // `icon` is rendered through the slot, so it must not end up as an attribute on the button.
    const { to, icon: _icon, ...blokProps } = props.blok;

    // Storyblok link fields carry an `anchor` alongside the destination. It was dropped
    // here, so an editor could fill one in and get a link that silently ignored it.
    const hash = to.anchor ? `#${to.anchor}` : '';

    if (to.linktype === 'url') {
      const url = `${to.url ?? ''}${hash}`;

      /**
       * A hash-only destination is an in-page jump — `#product-window`, or a link where
       * the editor set nothing but an anchor. Rendered as a real anchor so the browser
       * scrolls it natively; handing `#foo` to NuxtLink routes it through vue-router,
       * which resolves it against the current route and depends on scrollBehavior.
       */
      if (url.startsWith('#')) return { ...blokProps, tag: 'a', href: url };

      blokProps.to = url;
    }

    if (to.linktype === 'story') {
      const [, nuxtRoute = '/404'] = to.cached_url.split('pages');

      blokProps.to = { path: nuxtRoute, ...(hash ? { hash } : {}) };
    }

    return blokProps;
  });

  /**
   * In-page jumps scroll smoothly instead of snapping. Scoped to these buttons rather than a
   * global `scroll-behavior: smooth`, which would also animate Nuxt's scroll-to-top on every
   * route change. Falls back to the browser's own jump when the target is missing or the
   * visitor asked for reduced motion.
   */
  function scrollToInPageTarget(event: MouseEvent) {
    const href = button.value.tag === 'a' ? (button.value.href as string) : undefined;
    if (!href) return;

    const target = document.getElementById(href.slice(1));
    if (!target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.pushState(null, '', href);
  }
</script>
